import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Autocomplete,
  IconButton,
  Collapse,
  Tooltip,
  Snackbar,
  useMediaQuery
} from '@mui/material';
import { 
  LocationOn, 
  MyLocation, 
  Map as MapIcon,
  Search,
  ContentCopy,
  Fullscreen,
  FullscreenExit,
  ExpandMore,
  ExpandLess,
  Layers,
  GpsFixed
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { searchAddress, reverseGeocode, formatShortAddress } from '../services/geocodingService';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Satellite tile URL (Esri World Imagery - free)
const SATELLITE_TILE = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const SATELLITE_ATTR = '&copy; Esri, Maxar, Earthstar Geographics';
const STREET_TILE = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}';
const STREET_ATTR = 'Tiles &copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom, 2012';

// Map click handler + fly-to component
function LocationMarker({ position, onPositionChange, accuracy }) {
  const map = useMapEvents({
    click(e) {
      onPositionChange({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });

  useEffect(() => {
    if (position) {
      map.flyTo([position.lat, position.lng], Math.max(map.getZoom(), 15));
    }
  }, [position, map]);

  if (!position) return null;

  return (
    <>
      <Marker position={[position.lat, position.lng]}>
        <Popup>
          {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
        </Popup>
      </Marker>
      {accuracy && accuracy < 1000 && (
        <Circle 
          center={[position.lat, position.lng]} 
          radius={accuracy}
          pathOptions={{ color: '#2196f3', fillColor: '#2196f3', fillOpacity: 0.1, weight: 1 }}
        />
      )}
    </>
  );
}

// Component for re-centering the map
function FlyToButton({ position }) {
  const map = useMap();
  if (!position) return null;
  return null; // Used imperatively via map ref
}

// Tile layer switcher
function TileSwitch({ satellite }) {
  return (
    <TileLayer
      key={satellite ? 'sat' : 'street'}
      url={satellite ? SATELLITE_TILE : STREET_TILE}
      attribution={satellite ? SATELLITE_ATTR : STREET_ATTR}
    />
  );
}

export default function GeolocationInput({ 
  value, 
  onChange, 
  label = "GPS-Standort",
  fullWidth = false,
  disabled = false
}) {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [showMap, setShowMap] = useState(!isMobile);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [satellite, setSatellite] = useState(false);
  const [position, setPosition] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);
  const [error, setError] = useState(null);
  const [address, setAddress] = useState('');
  const [showManualCoords, setShowManualCoords] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [copySnackbar, setCopySnackbar] = useState(false);
  
  // Address search state
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const searchTimerRef = useRef(null);
  const mapRef = useRef(null);

  // Parse initial value
  useEffect(() => {
    if (value) {
      try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        if (parsed && parsed.lat && parsed.lng) {
          setPosition({ lat: parseFloat(parsed.lat), lng: parseFloat(parsed.lng) });
          setAddress(parsed.address || '');
          setManualLat(String(parsed.lat));
          setManualLng(String(parsed.lng));
        }
      } catch (e) {
        console.error('Error parsing geolocation value:', e);
      }
    }
  }, [value]);

  // Emit change to parent
  const emitChange = useCallback((pos, addr) => {
    onChange({
      lat: pos.lat,
      lng: pos.lng,
      address: addr || null
    });
  }, [onChange]);

  // Reverse geocode a position and update address
  const doReverseGeocode = useCallback(async (pos) => {
    setReverseLoading(true);
    try {
      const result = await reverseGeocode(pos.lat, pos.lng);
      if (result) {
        const short = formatShortAddress(result.address) || result.displayName;
        setAddress(short);
        emitChange(pos, short);
      }
    } catch (e) {
      // Reverse geocode failed silently — user can still enter address manually
    } finally {
      setReverseLoading(false);
    }
  }, [emitChange]);

  // Handle position changes (from map click or GPS)
  const handlePositionChange = useCallback((newPos, skipReverse = false) => {
    setPosition(newPos);
    setManualLat(String(newPos.lat.toFixed(6)));
    setManualLng(String(newPos.lng.toFixed(6)));
    emitChange(newPos, address);
    if (!skipReverse) {
      doReverseGeocode(newPos);
    }
  }, [address, emitChange, doReverseGeocode]);

  // Handle address text change (manual edit)
  const handleAddressManualChange = (newAddress) => {
    setAddress(newAddress);
    if (position) {
      emitChange(position, newAddress);
    }
  };

  // Get current GPS location
  const getCurrentLocation = () => {
    setLoading(true);
    setError(null);

    if (!navigator.geolocation) {
      setError('Geolocation wird von diesem Browser nicht unterstützt');
      setLoading(false);
      return;
    }

    // Check permission state if available
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then(result => {
        if (result.state === 'denied') {
          setError('Standortzugriff wurde verweigert. Bitte erlauben Sie den Zugriff in Ihren Browser-Einstellungen.');
          setLoading(false);
          return;
        }
        requestGPS();
      }).catch(() => {
        requestGPS();
      });
    } else {
      requestGPS();
    }
  };

  const requestGPS = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newPos = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude
        };
        setPosition(newPos);
        setAccuracy(pos.coords.accuracy);
        setManualLat(String(newPos.lat.toFixed(6)));
        setManualLng(String(newPos.lng.toFixed(6)));
        emitChange(newPos, address);
        doReverseGeocode(newPos);
        setLoading(false);
      },
      (err) => {
        let msg = 'Fehler beim Ermitteln der Position.';
        if (err.code === 1) msg = 'Standortzugriff wurde verweigert. Bitte erlauben Sie den Zugriff in Ihren Browser-Einstellungen.';
        else if (err.code === 2) msg = 'Position konnte nicht ermittelt werden. Bitte versuchen Sie es erneut.';
        else if (err.code === 3) msg = 'Zeitüberschreitung bei der Standortermittlung.';
        setError(msg);
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000
      }
    );
  };

  // Address search with debounce
  const handleSearchInput = (event, newValue) => {
    setSearchInput(newValue);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!newValue || newValue.length < 3) {
      setSearchOptions([]);
      return;
    }
    setSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const results = await searchAddress(newValue);
        setSearchOptions(results);
      } catch (e) {
        setSearchOptions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
  };

  // Address search result selected
  const handleSearchSelect = (event, selected) => {
    if (!selected) return;
    const newPos = { lat: selected.lat, lng: selected.lng };
    const short = formatShortAddress(selected.address) || selected.displayName;
    setPosition(newPos);
    setAddress(short);
    setManualLat(String(selected.lat.toFixed(6)));
    setManualLng(String(selected.lng.toFixed(6)));
    emitChange(newPos, short);
    setSearchInput('');
    if (!showMap) setShowMap(true);
  };

  // Manual coordinate input
  const handleManualCoordsApply = () => {
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setError('Ungültige Koordinaten. Breitengrad: -90 bis 90, Längengrad: -180 bis 180.');
      return;
    }
    setError(null);
    const newPos = { lat, lng };
    setPosition(newPos);
    emitChange(newPos, address);
    doReverseGeocode(newPos);
    if (!showMap) setShowMap(true);
  };

  // Copy coordinates to clipboard
  const handleCopy = () => {
    if (!position) return;
    navigator.clipboard.writeText(`${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`).then(() => {
      setCopySnackbar(true);
    });
  };

  const mapHeight = mapFullscreen ? 'calc(100vh - 200px)' : 300;

  return (
    <Box>
      {/* Label + current position */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
        
        {position && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<LocationOn />}
              label={`${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`}
              variant="outlined"
              size="small"
            />
            {accuracy && (
              <Chip label={`± ${Math.round(accuracy)}m`} size="small" variant="outlined" color="info" />
            )}
            <Tooltip title="Koordinaten kopieren">
              <IconButton size="small" onClick={handleCopy}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            {address && (
              <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
                {reverseLoading ? <CircularProgress size={12} sx={{ mr: 0.5 }} /> : null}
                {address}
              </Typography>
            )}
            {reverseLoading && !address && (
              <Typography variant="body2" color="text.secondary">
                <CircularProgress size={12} sx={{ mr: 0.5 }} /> Adresse wird geladen...
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Address search (autocomplete) */}
      <Autocomplete
        freeSolo
        options={searchOptions}
        getOptionLabel={(option) => typeof option === 'string' ? option : option.displayName}
        inputValue={searchInput}
        onInputChange={handleSearchInput}
        onChange={handleSearchSelect}
        loading={searchLoading}
        loadingText="Suche..."
        noOptionsText={searchInput.length >= 3 ? 'Keine Ergebnisse' : 'Min. 3 Zeichen eingeben'}
        disabled={disabled}
        filterOptions={(x) => x} // Don't filter client-side, Nominatim does it
        renderOption={(props, option) => (
          <li {...props} key={`${option.lat}-${option.lng}`}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <LocationOn color="action" sx={{ mt: 0.3 }} fontSize="small" />
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                {option.displayName}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Adresse suchen"
            placeholder="z.B. Brandenburger Tor, Berlin"
            size="small"
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: <Search color="action" fontSize="small" sx={{ mr: 0.5 }} />,
              endAdornment: (
                <>
                  {searchLoading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ mb: 1.5 }}
      />

      {/* Address display / manual override (shown when position is set) */}
      {position && (
        <TextField
          label="Adresse (bearbeitbar)"
          value={address}
          onChange={(e) => handleAddressManualChange(e.target.value)}
          fullWidth
          disabled={disabled}
          size="small"
          sx={{ mb: 1.5 }}
          helperText="Wird automatisch per Geocoding ausgefüllt. Sie können den Text anpassen."
        />
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <MyLocation />}
          onClick={getCurrentLocation}
          disabled={disabled || loading}
          size="small"
          color="primary"
        >
          {loading ? 'Ortung...' : 'Aktueller Standort'}
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<MapIcon />}
          onClick={() => setShowMap(!showMap)}
          disabled={disabled}
          size="small"
        >
          {showMap ? 'Karte ausblenden' : 'Karte anzeigen'}
        </Button>

        <Button
          variant="text"
          size="small"
          startIcon={showManualCoords ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowManualCoords(!showManualCoords)}
        >
          Koordinaten manuell
        </Button>
      </Box>

      {/* Manual coordinate input */}
      <Collapse in={showManualCoords}>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <TextField
              label="Breitengrad (Lat)"
              value={manualLat}
              onChange={(e) => setManualLat(e.target.value)}
              size="small"
              type="number"
              inputProps={{ step: 'any', min: -90, max: 90 }}
              sx={{ width: 160 }}
              disabled={disabled}
            />
            <TextField
              label="Längengrad (Lng)"
              value={manualLng}
              onChange={(e) => setManualLng(e.target.value)}
              size="small"
              type="number"
              inputProps={{ step: 'any', min: -180, max: 180 }}
              sx={{ width: 160 }}
              disabled={disabled}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleManualCoordsApply}
              startIcon={<GpsFixed />}
              disabled={disabled}
            >
              Übernehmen
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Map */}
      {showMap && (
        <Paper sx={{ mb: 1, overflow: 'hidden', position: mapFullscreen ? 'fixed' : 'relative', top: mapFullscreen ? 0 : 'auto', left: mapFullscreen ? 0 : 'auto', right: mapFullscreen ? 0 : 'auto', bottom: mapFullscreen ? 0 : 'auto', zIndex: mapFullscreen ? 1300 : 'auto' }}>
          {/* Map toolbar */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, p: 0.5, bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Tooltip title={satellite ? 'Straßenkarte' : 'Satellit'}>
              <IconButton size="small" onClick={() => setSatellite(!satellite)}>
                <Layers fontSize="small" />
              </IconButton>
            </Tooltip>
            {position && (
              <Tooltip title="Zum Standort zoomen">
                <IconButton size="small" onClick={() => {
                  if (mapRef.current) {
                    mapRef.current.flyTo([position.lat, position.lng], 15);
                  }
                }}>
                  <GpsFixed fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={mapFullscreen ? 'Vollbild beenden' : 'Vollbild'}>
              <IconButton size="small" onClick={() => setMapFullscreen(!mapFullscreen)}>
                {mapFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ height: mapHeight, width: '100%' }}>
            <MapContainer
              center={position ? [position.lat, position.lng] : [51.1657, 10.4515]}
              zoom={position ? 15 : 6}
              style={{ height: '100%', width: '100%' }}
              ref={mapRef}
            >
              <TileSwitch satellite={satellite} />
              <LocationMarker 
                position={position} 
                onPositionChange={handlePositionChange}
                accuracy={accuracy}
              />
            </MapContainer>
          </Box>
          <Box sx={{ p: 0.75, bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary">
              Klicken Sie auf die Karte, um einen Standort auszuwählen
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Copy confirmation */}
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message="Koordinaten kopiert"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}